import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import "./DomesticVisitors.css";
import { useFormContext } from "../../../context/Form_context";
import { useBasicContext } from "../../../context/Basic_context";
import Notification from "../../../utils/Notification";
import Loader from "../../../Common/loader/Loader";
import {
  FaUser,
  FaBuilding,
  FaEnvelope,
  FaWhatsapp,
  FaBriefcase,
  FaLocationDot,
  FaCity,
  FaGlobe,
  FaCircleCheck,
  FaTicket,
  FaCalendarDays,
  FaCompass,
  FaArrowRight,
  FaShieldHalved,
  FaGem,
  FaHeadset,
  FaFileContract,
  FaChevronDown,
} from "react-icons/fa6";

const isFieldEnabled = (format, key) => {
  if (!format) return false;
  const val = format[key];
  return val === "1" || val === 1 || val === true || val === "true";
};

const getCleanOptions = (optionsArray) => {
  if (!optionsArray || !Array.isArray(optionsArray)) return [];
  return optionsArray
    .map((opt) =>
      typeof opt === "string"
        ? opt.trim()
        : opt?.option || opt?.name || String(opt).trim(),
    )
    .filter((opt) => Boolean(opt) && opt !== "");
};

const isChildRequired = (child) => {
  if (!child) return false;
  return String(child.is_required) === "1" || child.is_required === 1;
};

const getChildOptions = (child) => {
  if (
    child?.visitor_format_child_option &&
    Array.isArray(child.visitor_format_child_option)
  ) {
    return child.visitor_format_child_option
      .filter((item) => {
        if (typeof item === "object" && item !== null) {
          const reqVal =
            item?.is_text_required ?? item?.is_required ?? item?.status;
          if (reqVal !== undefined && reqVal !== null) {
            return (
              String(reqVal) === "1" ||
              reqVal === 1 ||
              reqVal === true ||
              reqVal === "true"
            );
          }
        }
        return true;
      })
      .map((item) =>
        typeof item === "object" ? item?.option || item?.name : item,
      )
      .filter((opt) => Boolean(opt) && String(opt).trim() !== "");
  }
  return [];
};

const getFieldIcon = (label) => {
  const l = (label || "").toLowerCase();
  if (
    l.includes("website") ||
    l.includes("url") ||
    l.includes("link") ||
    l.includes("site")
  ) {
    return <FaGlobe className="dom-field-icon" />;
  }
  if (l.includes("address") || l.includes("location") || l.includes("street")) {
    return <FaLocationDot className="dom-field-icon" />;
  }
  if (
    l.includes("phone") ||
    l.includes("whatsapp") ||
    l.includes("mobile") ||
    l.includes("contact")
  ) {
    return <FaWhatsapp className="dom-field-icon dom-whatsapp-icon" />;
  }
  if (l.includes("email") || l.includes("mail")) {
    return <FaEnvelope className="dom-field-icon" />;
  }
  if (
    l.includes("company") ||
    l.includes("organization") ||
    l.includes("firm")
  ) {
    return <FaBuilding className="dom-field-icon" />;
  }
  if (
    l.includes("sector") ||
    l.includes("industry") ||
    l.includes("business") ||
    l.includes("category")
  ) {
    return <FaBriefcase className="dom-field-icon" />;
  }
  if (
    l.includes("date") ||
    l.includes("dob") ||
    l.includes("birth") ||
    l.includes("calendar")
  ) {
    return <FaCalendarDays className="dom-field-icon" />;
  }
  return <FaUser className="dom-field-icon" />;
};

const DomesticVisitors = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryId = searchParams.get("id");
  const stateId = location.state?.categoryId || location.state?.id;

  const {
    GetRegisterDetailExpo,
    GetRegisterForm,
    GetVisitorFormat,
    PostDomesticForm,
    register_form_data,
    register_form_loading,
    register_detail_expo_data,
    register_detail_expo_loading,
    visitor_format_data,
    submit_domestic_visitor_loading,
  } = useFormContext();

  const {
    GetCountry,
    get_country_data,
    get_country_loading,
    GetState,
    get_state_data,
    get_state_loading,
    GetCity,
    get_city_data,
    get_city_loading,
  } = useBasicContext();

  useEffect(() => {
    GetVisitorFormat("1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFormat = useMemo(() => {
    return visitor_format_data || {};
  }, [visitor_format_data]);

  useEffect(() => {
    if (
      isFieldEnabled(activeFormat, "country") ||
      isFieldEnabled(activeFormat, "country_code") ||
      isFieldEnabled(activeFormat, "state") ||
      isFieldEnabled(activeFormat, "city")
    ) {
      GetCountry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFormat]);

  useEffect(() => {
    const activeId = queryId || stateId;
    const isFormLoaded =
      register_form_data && Object.keys(register_form_data).length > 0;

    if (activeId) {
      GetRegisterDetailExpo(activeId);
    } else if (register_form_data?.registration_categories?.length > 0) {
      const categories = register_form_data.registration_categories;
      const matched = categories.find(
        (c) =>
          c.name?.toLowerCase().includes("domestic") ||
          c.tag?.toLowerCase().includes("domestic") ||
          c.name?.toLowerCase().includes("visitor"),
      );

      if (matched?.id) {
        GetRegisterDetailExpo(matched.id);
      }
    } else if (!isFormLoaded && !register_form_loading) {
      GetRegisterForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId, stateId, register_form_data]);

  const formattedDates = useMemo(() => {
    const fromStr = register_detail_expo_data?.from_date;
    const toStr = register_detail_expo_data?.to_date;
    if (!fromStr || !toStr) return "";

    const fromDate = dayjs(fromStr);
    const toDate = dayjs(toStr);

    if (!fromDate.isValid() || !toDate.isValid()) return "";

    if (
      fromDate.month() === toDate.month() &&
      fromDate.year() === toDate.year()
    ) {
      return `${fromDate.format("D")} – ${toDate.format("D MMMM YYYY")}`;
    } else {
      return `${fromDate.format("D MMM")} – ${toDate.format("D MMM YYYY")}`;
    }
  }, [
    register_detail_expo_data?.from_date,
    register_detail_expo_data?.to_date,
  ]);

  const [formData, setFormData] = useState({
    countryCode: "+91",
  });
  const [selectedProductCategories, setSelectedProductCategories] = useState(
    [],
  );
  const [formErrors, setFormErrors] = useState({});
  // Changing this key re-mounts the <form>, which also clears file inputs
  const [formKey, setFormKey] = useState(0);

  const currentCountryObj = useMemo(() => {
    if (!formData.countryCode || !Array.isArray(get_country_data)) return null;
    const targetCode = formData.countryCode.trim();
    return get_country_data.find((c) => {
      const rawCode =
        c?.phonecode ||
        c?.phone_code ||
        c?.country_code ||
        c?.isd_code ||
        c?.code ||
        "";
      const formattedCode = String(rawCode).startsWith("+")
        ? String(rawCode)
        : `+${rawCode}`;
      return formattedCode === targetCode;
    });
  }, [formData.countryCode, get_country_data]);

  const selectedSortName = useMemo(() => {
    const sortName =
      currentCountryObj?.sortname ||
      currentCountryObj?.sort_name ||
      currentCountryObj?.iso2 ||
      currentCountryObj?.iso_code ||
      currentCountryObj?.short_name ||
      (formData.countryCode === "+91" ? "IN" : "");
    return sortName ? sortName.toLowerCase() : "";
  }, [currentCountryObj, formData.countryCode]);

  useEffect(() => {
    if (formData.country) {
      GetState(formData.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.country]);

  useEffect(() => {
    if (formData.state) {
      GetCity(formData.state);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.state]);

  const resetForm = () => {
    setFormData({ countryCode: "+91" });
    setSelectedProductCategories([]);
    setFormErrors({});
    setFormKey((prev) => prev + 1);
  };

  const handleCategoryToggle = (categoryName) => {
    setSelectedProductCategories((prev) => {
      if (prev.includes(categoryName)) {
        return prev.filter((item) => item !== categoryName);
      } else {
        return [...prev, categoryName];
      }
    });
    if (formErrors.selectedProductCategories || formErrors.productCategory) {
      setFormErrors((prev) => ({
        ...prev,
        selectedProductCategories: "",
        productCategory: "",
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      if (name === "country") {
        updated.state = "";
        updated.city = "";
        if (value && Array.isArray(get_country_data)) {
          const matchedCountry = get_country_data.find(
            (c) =>
              c?.name === value ||
              c?.country_name === value ||
              String(c?.id) === String(value),
          );
          const rawCode =
            matchedCountry?.phonecode ||
            matchedCountry?.phone_code ||
            matchedCountry?.country_code ||
            matchedCountry?.isd_code ||
            matchedCountry?.code;
          if (rawCode) {
            updated.countryCode = String(rawCode).startsWith("+")
              ? String(rawCode)
              : `+${rawCode}`;
          }
        }
      }
      if (name === "state") {
        updated.city = "";
      }
      return updated;
    });

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const showFirstName =
    isFieldEnabled(activeFormat, "first_name") ||
    isFieldEnabled(activeFormat, "firstname");
  const showLastName =
    isFieldEnabled(activeFormat, "last_name") ||
    isFieldEnabled(activeFormat, "lastname");
  const showFullName = isFieldEnabled(activeFormat, "full_name");
  const showContact =
    isFieldEnabled(activeFormat, "contact") ||
    isFieldEnabled(activeFormat, "contact_no") ||
    isFieldEnabled(activeFormat, "mobile") ||
    isFieldEnabled(activeFormat, "whatsapp");
  const showEmail = isFieldEnabled(activeFormat, "email");
  const showCompany =
    isFieldEnabled(activeFormat, "company") ||
    isFieldEnabled(activeFormat, "company_name");
  const showDesignation =
    isFieldEnabled(activeFormat, "designation") ||
    isFieldEnabled(activeFormat, "designation_name");
  const showProductCategory = isFieldEnabled(activeFormat, "product_category");
  const showBusinessNature =
    isFieldEnabled(activeFormat, "business_nature") ||
    isFieldEnabled(activeFormat, "nature_of_business");
  const showBusinessSector = isFieldEnabled(activeFormat, "business_sector");
  const showCountryCode = isFieldEnabled(activeFormat, "country_code");
  const showCountry = isFieldEnabled(activeFormat, "country");
  const showState = isFieldEnabled(activeFormat, "state");
  const showCity = isFieldEnabled(activeFormat, "city");

  const validate = () => {
    const errors = {};

    if (showFirstName && !formData.firstName?.trim()) {
      errors.firstName = "Enter First Name";
    }
    if (showLastName && !formData.lastName?.trim()) {
      errors.lastName = "Enter Last Name";
    }
    if (showFullName && !formData.fullName?.trim()) {
      errors.fullName = "Enter Full Name";
    }
    if (showCountry && !formData.country) {
      errors.country = "Select Country";
    }
    if (
      showContact &&
      !formData.contact?.trim() &&
      !formData.whatsapp?.trim()
    ) {
      errors.contact = "Enter Contact Number";
    }
    if (showEmail) {
      const emailVal = formData.email?.trim();
      if (!emailVal) {
        errors.email = "Enter Email Address";
      } else if (!/\S+@\S+\.\S+/.test(emailVal)) {
        errors.email = "Enter valid Email Address";
      }
    }
    if (showCompany && !formData.companyName?.trim()) {
      errors.companyName = "Enter Company Name";
    }
    if (showDesignation && !formData.designation?.trim()) {
      errors.designation = "Enter Designation";
    }
    if (showProductCategory && selectedProductCategories.length === 0) {
      errors.selectedProductCategories = "Select at least one Product Category";
    }
    if (showBusinessNature && !formData.businessNature) {
      errors.businessNature = "Select Nature of Business";
    }
    if (showBusinessSector && !formData.businessSector) {
      errors.businessSector = "Select Business Sector";
    }
    if (showState && !formData.state) {
      errors.state = "Select State";
    }
    if (showCity && !formData.city?.trim()) {
      errors.city = "Select or Enter City";
    }

    if (
      activeFormat?.visitor_format_child &&
      Array.isArray(activeFormat.visitor_format_child)
    ) {
      activeFormat.visitor_format_child
        .filter((child) => child && isChildRequired(child))
        .forEach((child) => {
          const fieldKey = `child_${child.id}`;
          const val = formData[fieldKey];
          const isSelectType =
            child.type === "select" ||
            child.type === "dropdown" ||
            child.type === "radio" ||
            child.type === "checkbox" ||
            child.type === "file" ||
            child.type === "image" ||
            child.type === "date" ||
            child.type === "datetime";
          const actionText = isSelectType ? "Select" : "Enter";

          if (!val || (typeof val === "string" && !val.trim())) {
            errors[fieldKey] = `${actionText} ${child.label}`;
          } else if (Array.isArray(val) && val.length === 0) {
            errors[fieldKey] = `${actionText} ${child.label}`;
          }
        });
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validate();
    const errorList = Object.values(errors);
    if (errorList.length > 0) {
      Notification("error", "Missing Info!", errorList[0], 3);
      return;
    }

    // ---------- Build FormData as per API (Postman) ----------
    const fd = new FormData();

    // full_name
    const fullName =
      formData.fullName?.trim() ||
      `${formData.firstName || ""} ${formData.lastName || ""}`.trim();
    if (fullName) fd.append("full_name", fullName);

    // email
    if (formData.email?.trim()) fd.append("email", formData.email.trim());

    // contact
    const contactVal = (formData.contact || formData.whatsapp || "").trim();
    if (contactVal) fd.append("contact", contactVal);

    // company
    if (formData.companyName?.trim()) {
      fd.append("company", formData.companyName.trim());
    }

    // designation
    if (formData.designation?.trim()) {
      fd.append("designation", formData.designation.trim());
    }

    // country (id)
    const selectedCountry = Array.isArray(get_country_data)
      ? get_country_data.find(
          (c) =>
            String(c?.id) === String(formData.country) ||
            c?.name === formData.country,
        )
      : null;
    const countryValToAppend = selectedCountry?.id || formData.country;
    if (countryValToAppend) {
      fd.append("country", countryValToAppend);
    }

    // country_code
    if (formData.countryCode?.trim()) {
      fd.append("country_code", formData.countryCode.trim());
    }

    // state (id)
    if (formData.state) fd.append("state", formData.state);

    // city (id)
    const selectedCity = Array.isArray(get_city_data)
      ? get_city_data.find((ct) => ct?.name === formData.city)
      : null;
    if (selectedCity?.id || formData.city) {
      fd.append("city", selectedCity?.id || formData.city);
    }

    // business_sector
    if (isFieldEnabled(activeFormat, "business_sector")) {
      if (formData.businessSector) {
        fd.append("business_sector_options[]", formData.businessSector);
      }
    }

    // business_nature
    if (
      isFieldEnabled(activeFormat, "business_nature") ||
      isFieldEnabled(activeFormat, "nature_of_business")
    ) {
      if (formData.businessNature) {
        fd.append("business_nature_options[]", formData.businessNature);
      }
    }

    // product_category_option[]
    if (selectedProductCategories && selectedProductCategories.length > 0) {
      selectedProductCategories.forEach((cat) => {
        fd.append("product_category_option[]", cat);
      });
    } else if (formData.productCategory) {
      fd.append("product_category_option[]", formData.productCategory);
    }

    // child_answers[id]
    if (
      activeFormat?.visitor_format_child &&
      Array.isArray(activeFormat.visitor_format_child)
    ) {
      activeFormat.visitor_format_child.filter(Boolean).forEach((child) => {
        const val = formData[`child_${child.id}`];
        if (val === undefined || val === null || val === "") return;

        if (Array.isArray(val)) {
          val.forEach((v) => fd.append(`child_answers[${child.id}][]`, v));
        } else {
          fd.append(`child_answers[${child.id}]`, val);
        }
      });
    }

    // Wait for API result -> clear the form only on success
    const isSuccess = await PostDomesticForm(fd);
    if (isSuccess) {
      resetForm();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const isDomesticData =
    !register_detail_expo_data?.registration_category ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("domestic") ||
    register_detail_expo_data?.registration_category?.tag
      ?.toLowerCase()
      .includes("domestic") ||
    register_detail_expo_data?.registration_category?.name
      ?.toLowerCase()
      .includes("visitor");

  if (
    register_detail_expo_loading ||
    register_form_loading ||
    !register_detail_expo_data ||
    Object.keys(register_detail_expo_data).length === 0 ||
    !isDomesticData
  ) {
    return <Loader fullScreen={true} text="Loading Domestic Visitors..." />;
  }

  const businessSectorOpts = getCleanOptions(
    activeFormat.business_sector_option || activeFormat.business_sector_options,
  );
  const businessNatureOpts = getCleanOptions(
    activeFormat.business_nature_option ||
      activeFormat.business_nature_options ||
      activeFormat.nature_of_business_option ||
      activeFormat.nature_of_business_options,
  );
  const productCategoryOpts = getCleanOptions(
    activeFormat.product_category_option ||
      activeFormat.product_category_options,
  );

  return (
    <div className="dom-vis-page">
      <Helmet>
        <title>Domestic Visitors Registration | Bharat Buildcon</title>
      </Helmet>
      {/* Background Decorative Mesh Shapes */}
      <div className="dom-bg-glow dom-bg-glow--saffron" />
      <div className="dom-bg-glow dom-bg-glow--blue" />

      {/* ---------------- BREADCRUMB & HERO BANNER ---------------- */}
      <section className="dom-hero-section">
        <div className="dom-container">
          {/* Dynamic Hero Banner Frame with Image & Unique Animations */}
          <div className="dom-hero-banner-card">
            <div className="dom-hero-bg-media">
              <img
                src={
                  register_detail_expo_data?.registration_detail
                    ?.image_full_path
                }
                alt="Bharat Buildcon Expo"
                className="dom-hero-img"
              />
              <div className="dom-hero-overlay" />
              <div className="dom-hero-scan-line" />
              <div className="dom-hero-grid-pattern" />
            </div>

            <div className="dom-hero-content">
              <div className="dom-ribbon-badge">
                <span className="dom-ribbon-spark" />
                <FaTicket className="dom-ribbon-icon" />
                <span>
                  {register_detail_expo_data?.registration_detail
                    ?.reg_detail_sub_heading || ""}
                </span>
              </div>
              <h1 className="dom-hero-title">
                {register_detail_expo_data?.registration_detail
                  ?.reg_detail_heading
                  ? (() => {
                      const words =
                        register_detail_expo_data?.registration_detail?.reg_detail_heading
                          .trim()
                          .split(" ");
                      if (words.length === 1) {
                        return (
                          <span className="dom-shimmer-text">{words[0]}</span>
                        );
                      }
                      const mainText = words.slice(0, -1).join(" ");
                      const lastWord = words[words.length - 1];
                      return (
                        <>
                          {mainText}{" "}
                          <span className="dom-shimmer-text">{lastWord}</span>
                        </>
                      );
                    })()
                  : null}
              </h1>
            </div>

            <div className="dom-info-strip">
              {Number(
                register_detail_expo_data?.registration_category
                  ?.registration_free,
              ) === 1 && (
                <>
                  <div className="dom-info-item">
                    <div className="dom-info-icon-box">
                      <FaTicket />
                    </div>
                    <div className="dom-info-text">
                      <span className="dom-info-label">Entry Pass</span>
                      <span className="dom-info-val">Free Entry Pass</span>
                    </div>
                  </div>

                  <div className="dom-info-divider" />
                </>
              )}

              <div className="dom-info-item">
                <div className="dom-info-icon-box">
                  <FaCalendarDays />
                </div>
                <div className="dom-info-text">
                  <span className="dom-info-label">Dates</span>
                  <span className="dom-info-val">{formattedDates || ""}</span>
                </div>
              </div>

              <div className="dom-info-divider" />

              <div className="dom-info-item">
                <div className="dom-info-icon-box">
                  <FaCompass />
                </div>
                <div className="dom-info-text">
                  <span className="dom-info-label">Venue</span>
                  <span className="dom-info-val">
                    {register_detail_expo_data?.venue_name || ""}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- MAIN FORM & SIDEBAR SECTION ---------------- */}
      <section className="dom-main-section">
        <div className="dom-container">
          <div className="dom-layout-grid">
            <div className="dom-main-col">
              <div className="dom-card dom-form-card dom-form-elevated">
                <div className="dom-card-header">
                  <div className="dom-header-top">
                    <span className="dom-subhead">
                      TELL US LITTLE MORE ABOUT YOU
                    </span>
                  </div>
                  <h2 className="dom-card-title">Registration Form</h2>
                  <p className="dom-card-desc">
                    Please fill out your details to receive an instant digital
                    visitor pass via Email and WhatsApp.
                  </p>
                </div>

                <form
                  key={formKey}
                  onSubmit={handleSubmit}
                  className="dom-form"
                >
                  <div className="dom-form-grid">
                    {/* First Name */}
                    {showFirstName && (
                      <div className="dom-field-group">
                        <label htmlFor="firstName" className="dom-label">
                          First Name <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaUser className="dom-field-icon" />
                          <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName || ""}
                            onChange={handleChange}
                            placeholder="Enter first name"
                            className={`dom-input ${
                              formData.firstName?.trim()
                                ? "dom-input-valid"
                                : ""
                            }`}
                          />
                        </div>
                        {formErrors.firstName && (
                          <span className="dom-error-msg">
                            {formErrors.firstName}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Last Name */}
                    {showLastName && (
                      <div className="dom-field-group">
                        <label htmlFor="lastName" className="dom-label">
                          Last Name <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaUser className="dom-field-icon" />
                          <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName || ""}
                            onChange={handleChange}
                            placeholder="Enter last name"
                            className={`dom-input ${
                              formData.lastName?.trim() ? "dom-input-valid" : ""
                            }`}
                          />
                        </div>
                        {formErrors.lastName && (
                          <span className="dom-error-msg">
                            {formErrors.lastName}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Full Name */}
                    {showFullName && (
                      <div className="dom-field-group">
                        <label htmlFor="fullName" className="dom-label">
                          Full Name <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaUser className="dom-field-icon" />
                          <input
                            type="text"
                            id="fullName"
                            name="fullName"
                            value={formData.fullName || ""}
                            onChange={handleChange}
                            placeholder="Enter full name"
                            className={`dom-input ${
                              formData.fullName?.trim() ? "dom-input-valid" : ""
                            }`}
                          />
                        </div>
                        {formErrors.fullName && (
                          <span className="dom-error-msg">
                            {formErrors.fullName}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Company Name */}
                    {showCompany && (
                      <div className="dom-field-group">
                        <label htmlFor="companyName" className="dom-label">
                          Company Name <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaBuilding className="dom-field-icon" />
                          <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName || ""}
                            onChange={handleChange}
                            placeholder="Enter company name"
                            className={`dom-input ${
                              formData.companyName?.trim()
                                ? "dom-input-valid"
                                : ""
                            }`}
                          />
                        </div>
                        {formErrors.companyName && (
                          <span className="dom-error-msg">
                            {formErrors.companyName}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Work Email Address */}
                    {showEmail && (
                      <div className="dom-field-group">
                        <label htmlFor="email" className="dom-label">
                          Work Email Address{" "}
                          <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaEnvelope className="dom-field-icon" />
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email || ""}
                            onChange={handleChange}
                            placeholder="name@company.com"
                            className={`dom-input ${
                              formData.email?.trim() ? "dom-input-valid" : ""
                            }`}
                          />
                        </div>
                        {formErrors.email && (
                          <span className="dom-error-msg">
                            {formErrors.email}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Contact / Whatsapp Number with integrated Country Code inside 1 single input box */}
                    {(showContact || showCountryCode) && (
                      <div className="dom-field-group">
                        <label htmlFor="contact" className="dom-label">
                          Participant Mobile/Whatsapp number
                          <span className="dom-required">*</span>
                        </label>
                        <div className="dom-unified-phone-container">
                          {/* Country Code embedded section with real Flag image icon and API phonecode dropdown */}
                          {showCountryCode && (
                            <>
                              <div className="dom-country-code-embed">
                                {selectedSortName ? (
                                  <img
                                    src={`https://flagcdn.com/w40/${selectedSortName}.png`}
                                    alt={selectedSortName}
                                    className="dom-embed-flag-img"
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <FaGlobe className="dom-embed-icon" />
                                )}
                                <span className="dom-embed-code-text">
                                  {formData.countryCode || "+91"}
                                </span>
                                <FaChevronDown className="dom-embed-chevron" />
                                <select
                                  id="countryCode"
                                  name="countryCode"
                                  value={formData.countryCode || "+91"}
                                  onChange={handleChange}
                                  className="dom-embed-cc-select"
                                  disabled={get_country_loading}
                                >
                                  {Array.isArray(get_country_data) &&
                                    get_country_data.map((c, idx) => {
                                      const rawCode =
                                        c?.phonecode ||
                                        c?.phone_code ||
                                        c?.country_code ||
                                        c?.isd_code ||
                                        c?.code ||
                                        "";
                                      if (!rawCode) return null;
                                      const formattedCode = String(
                                        rawCode,
                                      ).startsWith("+")
                                        ? String(rawCode)
                                        : `+${rawCode}`;
                                      const countryName =
                                        c?.name || c?.country_name || "";
                                      const displayLabel = countryName
                                        ? `${formattedCode} (${countryName})`
                                        : formattedCode;
                                      return (
                                        <option
                                          key={c?.id || idx}
                                          value={formattedCode}
                                        >
                                          {displayLabel}
                                        </option>
                                      );
                                    })}
                                </select>
                              </div>

                              {/* Vertical Divider */}
                              <div className="dom-phone-divider" />
                            </>
                          )}

                          {/* Mobile Number embedded section */}
                          <div className="dom-mobile-number-embed">
                            <FaWhatsapp className="dom-embed-icon dom-whatsapp-icon" />
                            <input
                              type="tel"
                              id="contact"
                              name="contact"
                              value={
                                formData.contact || formData.whatsapp || ""
                              }
                              onChange={(e) => {
                                const val = e.target.value;
                                setFormData((prev) => ({
                                  ...prev,
                                  contact: val,
                                  whatsapp: val,
                                }));
                                if (formErrors.contact) {
                                  setFormErrors((prev) => ({
                                    ...prev,
                                    contact: "",
                                  }));
                                }
                              }}
                              placeholder="e.g. 98765 43210"
                              className="dom-embed-mobile-input"
                            />
                          </div>
                        </div>
                        {formErrors.contact && (
                          <span className="dom-error-msg">
                            {formErrors.contact}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Designation */}
                    {showDesignation && (
                      <div className="dom-field-group">
                        <label htmlFor="designation" className="dom-label">
                          Designation <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaUser className="dom-field-icon" />
                          <input
                            type="text"
                            id="designation"
                            name="designation"
                            value={formData.designation || ""}
                            onChange={handleChange}
                            placeholder="Enter designation"
                            className={`dom-input ${
                              formData.designation?.trim()
                                ? "dom-input-valid"
                                : ""
                            }`}
                          />
                        </div>
                        {formErrors.designation && (
                          <span className="dom-error-msg">
                            {formErrors.designation}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Business Nature */}
                    {showBusinessNature && (
                      <div className="dom-field-group">
                        <label htmlFor="businessNature" className="dom-label">
                          Nature Of Business{" "}
                          <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaUser className="dom-field-icon" />
                          <select
                            id="businessNature"
                            name="businessNature"
                            value={formData.businessNature || ""}
                            onChange={handleChange}
                            className={`dom-select ${
                              formData.businessNature ? "dom-input-valid" : ""
                            }`}
                          >
                            <option value="">Select Business Category</option>
                            {businessNatureOpts.map((nature, index) => (
                              <option key={index} value={nature}>
                                {nature}
                              </option>
                            ))}
                          </select>
                        </div>
                        {formErrors.businessNature && (
                          <span className="dom-error-msg">
                            {formErrors.businessNature}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Business Sector */}
                    {showBusinessSector && (
                      <div className="dom-field-group">
                        <label htmlFor="businessSector" className="dom-label">
                          Main Business Sector{" "}
                          <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaBriefcase className="dom-field-icon" />
                          <select
                            id="businessSector"
                            name="businessSector"
                            value={formData.businessSector || ""}
                            onChange={handleChange}
                            className={`dom-select ${
                              formData.businessSector ? "dom-input-valid" : ""
                            }`}
                          >
                            <option value="">
                              Select main business sector
                            </option>
                            {businessSectorOpts.map((sector, index) => (
                              <option key={index} value={sector}>
                                {sector}
                              </option>
                            ))}
                          </select>
                        </div>
                        {formErrors.businessSector && (
                          <span className="dom-error-msg">
                            {formErrors.businessSector}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Country */}
                    {showCountry && (
                      <div className="dom-field-group">
                        <label htmlFor="country" className="dom-label">
                          Country <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaGlobe className="dom-field-icon" />
                          <select
                            id="country"
                            name="country"
                            value={formData.country || ""}
                            onChange={handleChange}
                            disabled={get_country_loading}
                            className={`dom-select ${
                              formData.country ? "dom-input-valid" : ""
                            }`}
                          >
                            <option value="">
                              {get_country_loading
                                ? "Loading Countries..."
                                : "Select Country"}
                            </option>
                            {get_country_data &&
                              Array.isArray(get_country_data) &&
                              get_country_data.map((c, idx) => {
                                const cName =
                                  typeof c === "string"
                                    ? c
                                    : c?.name || c?.country_name || "";
                                const cId = c?.id || cName || idx;
                                return (
                                  <option key={cId} value={c?.id || cName}>
                                    {cName}
                                  </option>
                                );
                              })}
                          </select>
                        </div>
                        {formErrors.country && (
                          <span className="dom-error-msg">
                            {formErrors.country}
                          </span>
                        )}
                      </div>
                    )}

                    {/* State */}
                    {showState && (
                      <div className="dom-field-group">
                        <label htmlFor="state" className="dom-label">
                          State <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaLocationDot className="dom-field-icon" />
                          <select
                            id="state"
                            name="state"
                            value={formData.state || ""}
                            onChange={handleChange}
                            disabled={
                              get_state_loading ||
                              (showCountry && !formData.country)
                            }
                            className={`dom-select ${
                              formData.state ? "dom-input-valid" : ""
                            }`}
                          >
                            <option value="">
                              {showCountry && !formData.country
                                ? "Select Country First"
                                : get_state_loading
                                  ? "Loading States..."
                                  : "Select State"}
                            </option>
                            {get_state_data &&
                              Array.isArray(get_state_data) &&
                              get_state_data.map((st) => (
                                <option key={st.id || st.name} value={st.id}>
                                  {st.name}
                                </option>
                              ))}
                          </select>
                        </div>
                        {formErrors.state && (
                          <span className="dom-error-msg">
                            {formErrors.state}
                          </span>
                        )}
                      </div>
                    )}

                    {/* City */}
                    {showCity && (
                      <div className="dom-field-group">
                        <label htmlFor="city" className="dom-label">
                          City <span className="dom-required">*</span>
                        </label>
                        <div className="dom-input-wrapper">
                          <FaCity className="dom-field-icon" />
                          {showState ? (
                            <select
                              id="city"
                              name="city"
                              value={formData.city || ""}
                              onChange={handleChange}
                              disabled={!formData.state || get_city_loading}
                              className={`dom-select ${
                                formData.city ? "dom-input-valid" : ""
                              }`}
                            >
                              <option value="">
                                {!formData.state
                                  ? "Select State First"
                                  : get_city_loading
                                    ? "Loading Cities..."
                                    : "Select City"}
                              </option>
                              {formData.state &&
                                get_city_data &&
                                get_city_data.map((ct) => (
                                  <option
                                    key={ct.id || ct.name}
                                    value={ct.name}
                                  >
                                    {ct.name}
                                  </option>
                                ))}
                            </select>
                          ) : (
                            <input
                              type="text"
                              id="city"
                              name="city"
                              value={formData.city || ""}
                              onChange={handleChange}
                              placeholder="Enter city"
                              className={`dom-input ${
                                formData.city?.trim() ? "dom-input-valid" : ""
                              }`}
                            />
                          )}
                        </div>
                        {formErrors.city && (
                          <span className="dom-error-msg">
                            {formErrors.city}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Product Category */}
                    {showProductCategory && (
                      <div className="dom-field-group dom-field-group--full">
                        <label className="dom-label">
                          Product Category{" "}
                          <span className="dom-required">*</span>
                        </label>
                        <div className="dom-category-checkbox-grid">
                          {productCategoryOpts.map((category, index) => {
                            const isChecked =
                              selectedProductCategories.includes(category);
                            return (
                              <label
                                key={index}
                                className={`dom-category-checkbox-item ${isChecked ? "selected" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() =>
                                    handleCategoryToggle(category)
                                  }
                                  className="dom-checkbox-input"
                                />
                                <span className="dom-checkbox-label">
                                  {category}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                        {(formErrors.selectedProductCategories ||
                          formErrors.productCategory) && (
                          <span className="dom-error-msg">
                            {formErrors.selectedProductCategories ||
                              formErrors.productCategory}
                          </span>
                        )}
                      </div>
                    )}

                    {/* DYNAMIC CHILD FIELDS (from visitor_format_child) */}
                    {activeFormat?.visitor_format_child &&
                      Array.isArray(activeFormat.visitor_format_child) &&
                      activeFormat.visitor_format_child
                        .filter(Boolean)
                        .map((child) => {
                          const fieldKey = `child_${child.id}`;
                          const fieldOptions = getChildOptions(child);
                          const isReq = isChildRequired(child);
                          const fieldIcon = getFieldIcon(child.label);

                          // Select Dropdown Type
                          if (
                            child.type === "select" ||
                            child.type === "dropdown"
                          ) {
                            return (
                              <div className="dom-field-group" key={child.id}>
                                <label htmlFor={fieldKey} className="dom-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="dom-required">*</span>
                                  )}
                                </label>
                                <div className="dom-input-wrapper">
                                  {fieldIcon}
                                  <select
                                    id={fieldKey}
                                    name={fieldKey}
                                    value={formData[fieldKey] || ""}
                                    onChange={handleChange}
                                    className={`dom-select ${
                                      formData[fieldKey]
                                        ? "dom-input-valid"
                                        : ""
                                    }`}
                                  >
                                    <option value="">
                                      Select {child.label}
                                    </option>
                                    {fieldOptions.map((opt, idx) => (
                                      <option key={idx} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                {formErrors[fieldKey] && (
                                  <span className="dom-error-msg">
                                    {formErrors[fieldKey]}
                                  </span>
                                )}
                              </div>
                            );
                          }

                          // Textarea Type
                          if (child.type === "textarea") {
                            return (
                              <div
                                className="dom-field-group dom-field-wide"
                                key={child.id}
                              >
                                <label htmlFor={fieldKey} className="dom-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="dom-required">*</span>
                                  )}
                                </label>
                                <div className="dom-input-wrapper">
                                  <FaFileContract
                                    className="dom-field-icon"
                                    style={{ top: "18px" }}
                                  />
                                  <textarea
                                    id={fieldKey}
                                    name={fieldKey}
                                    value={formData[fieldKey] || ""}
                                    onChange={handleChange}
                                    placeholder={`Enter ${child.label.toLowerCase()}`}
                                    className={`dom-input dom-textarea ${
                                      formData[fieldKey]?.trim()
                                        ? "dom-input-valid"
                                        : ""
                                    }`}
                                    rows={3}
                                  />
                                </div>
                                {formErrors[fieldKey] && (
                                  <span className="dom-error-msg">
                                    {formErrors[fieldKey]}
                                  </span>
                                )}
                              </div>
                            );
                          }

                          // Radio Type
                          if (child.type === "radio") {
                            return (
                              <div
                                className={`dom-field-group ${fieldOptions.length > 3 ? "dom-field-wide" : ""}`}
                                key={child.id}
                              >
                                <label className="dom-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="dom-required">*</span>
                                  )}
                                </label>
                                <div className="dom-radio-group">
                                  {fieldOptions.map((opt, idx) => (
                                    <label key={idx} className="dom-radio-item">
                                      <input
                                        type="radio"
                                        name={fieldKey}
                                        value={opt}
                                        checked={formData[fieldKey] === opt}
                                        onChange={handleChange}
                                      />
                                      <span>{opt}</span>
                                    </label>
                                  ))}
                                </div>
                                {formErrors[fieldKey] && (
                                  <span className="dom-error-msg">
                                    {formErrors[fieldKey]}
                                  </span>
                                )}
                              </div>
                            );
                          }

                          // Checkbox Type
                          if (child.type === "checkbox") {
                            return (
                              <div
                                className={`dom-field-group ${fieldOptions.length > 3 ? "dom-field-wide" : ""}`}
                                key={child.id}
                              >
                                <label className="dom-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="dom-required">*</span>
                                  )}
                                </label>
                                <div className="dom-checkbox-group">
                                  {fieldOptions.map((opt, idx) => {
                                    const selectedVals =
                                      formData[fieldKey] || [];
                                    const isChecked =
                                      Array.isArray(selectedVals) &&
                                      selectedVals.includes(opt);
                                    return (
                                      <label
                                        key={idx}
                                        className="dom-checkbox-item"
                                      >
                                        <input
                                          type="checkbox"
                                          name={fieldKey}
                                          value={opt}
                                          checked={isChecked}
                                          onChange={(e) => {
                                            const curr = Array.isArray(
                                              formData[fieldKey],
                                            )
                                              ? formData[fieldKey]
                                              : [];
                                            const next = e.target.checked
                                              ? [...curr, opt]
                                              : curr.filter((v) => v !== opt);
                                            setFormData((prev) => ({
                                              ...prev,
                                              [fieldKey]: next,
                                            }));
                                            if (formErrors[fieldKey]) {
                                              setFormErrors((prev) => ({
                                                ...prev,
                                                [fieldKey]: "",
                                              }));
                                            }
                                          }}
                                        />
                                        <span>{opt}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                                {formErrors[fieldKey] && (
                                  <span className="dom-error-msg">
                                    {formErrors[fieldKey]}
                                  </span>
                                )}
                              </div>
                            );
                          }

                          // File / Image Input Type
                          if (child.type === "file" || child.type === "image") {
                            return (
                              <div className="dom-field-group" key={child.id}>
                                <label htmlFor={fieldKey} className="dom-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="dom-required">*</span>
                                  )}
                                </label>
                                <div className="dom-file-input-wrapper">
                                  <input
                                    type="file"
                                    id={fieldKey}
                                    name={fieldKey}
                                    accept="image/*,.pdf"
                                    onChange={(e) => {
                                      const selectedFile = e.target.files[0];
                                      setFormData((prev) => ({
                                        ...prev,
                                        [fieldKey]: selectedFile || null,
                                      }));
                                      if (formErrors[fieldKey]) {
                                        setFormErrors((prev) => ({
                                          ...prev,
                                          [fieldKey]: "",
                                        }));
                                      }
                                    }}
                                    className="dom-file-native-input"
                                  />
                                  <button
                                    type="button"
                                    className="dom-btn-choose-file"
                                  >
                                    Choose File
                                  </button>
                                  <span className="dom-file-chosen-text">
                                    {formData[fieldKey]?.name
                                      ? formData[fieldKey].name
                                      : "No file chosen"}
                                  </span>
                                </div>
                                {formErrors[fieldKey] && (
                                  <span className="dom-error-msg">
                                    {formErrors[fieldKey]}
                                  </span>
                                )}
                              </div>
                            );
                          }

                          // Date Input Type
                          if (
                            child.type === "date" ||
                            child.type === "datetime"
                          ) {
                            return (
                              <div className="dom-field-group" key={child.id}>
                                <label htmlFor={fieldKey} className="dom-label">
                                  {child.label}
                                  {isReq && (
                                    <span className="dom-required">*</span>
                                  )}
                                </label>
                                <div className="dom-input-wrapper">
                                  <FaCalendarDays className="dom-field-icon" />
                                  <input
                                    type="date"
                                    id={fieldKey}
                                    name={fieldKey}
                                    value={formData[fieldKey] || ""}
                                    onChange={handleChange}
                                    className={`dom-input ${formData[fieldKey] ? "dom-input-valid" : ""}`}
                                  />
                                </div>
                                {formErrors[fieldKey] && (
                                  <span className="dom-error-msg">
                                    {formErrors[fieldKey]}
                                  </span>
                                )}
                              </div>
                            );
                          }

                          // Standard Text / Default Input Type (e.g. text, email, tel, number)
                          const isWide = child.label
                            ?.toLowerCase()
                            .includes("address");
                          return (
                            <div
                              className={`dom-field-group ${
                                isWide ? "dom-field-wide" : ""
                              }`}
                              key={child.id}
                            >
                              <label htmlFor={fieldKey} className="dom-label">
                                {child.label}
                                {isReq && (
                                  <span className="dom-required">*</span>
                                )}
                              </label>
                              <div className="dom-input-wrapper">
                                {fieldIcon}
                                <input
                                  type={
                                    child.type === "email"
                                      ? "email"
                                      : child.type === "number"
                                        ? "number"
                                        : "text"
                                  }
                                  id={fieldKey}
                                  name={fieldKey}
                                  value={formData[fieldKey] || ""}
                                  onChange={handleChange}
                                  placeholder={`Enter ${child.label.toLowerCase()}`}
                                  className={`dom-input ${
                                    formData[fieldKey]?.trim()
                                      ? "dom-input-valid"
                                      : ""
                                  }`}
                                />
                              </div>
                              {formErrors[fieldKey] && (
                                <span className="dom-error-msg">
                                  {formErrors[fieldKey]}
                                </span>
                              )}
                            </div>
                          );
                        })}
                  </div>

                  {/* Submit Button */}
                  <div className="dom-form-actions">
                    <button
                      type="submit"
                      disabled={submit_domestic_visitor_loading}
                      className="dom-btn-submit dom-btn-animated"
                    >
                      <span className="dom-btn-shine" />
                      {submit_domestic_visitor_loading ? (
                        <span className="dom-btn-loading">
                          <span className="dom-spinner" /> Submitting...
                        </span>
                      ) : (
                        <>
                          <span>SUBMIT</span>
                          <FaArrowRight className="dom-btn-arrow" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Column: Information & Perks Sidebar */}
            <div className="dom-sidebar-col">
              {/* Event Perks Card */}
              {register_detail_expo_data?.registration_detail?.why_registers &&
                register_detail_expo_data?.registration_detail?.why_registers
                  .length > 0 && (
                  <div className="dom-card dom-sidebar-card">
                    <div className="dom-sb-header">
                      <FaGem className="dom-sb-icon" />
                      <h3>Why Register Today?</h3>
                    </div>

                    <div className="dom-perks-list">
                      {register_detail_expo_data?.registration_detail?.why_registers
                        .filter((item) => item.name || item.description)
                        .map((item, index) => (
                          <div className="dom-perk-item" key={item.id || index}>
                            <div className="dom-perk-bullet">
                              <FaCircleCheck />
                            </div>
                            <div>
                              {item.name && <h4>{item.name}</h4>}
                              {item.description && <p>{item.description}</p>}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              {/* Support & Contact Card */}
              <div className="dom-card dom-sidebar-card dom-help-card">
                <div className="dom-sb-header">
                  <FaHeadset className="dom-sb-icon dom-sb-icon-orange" />
                  <h3>Registration Helpdesk</h3>
                </div>
                {(register_detail_expo_data?.registration_detail
                  ?.registration_heplpdesk_description ||
                  register_detail_expo_data?.registration_heplpdesk_description) && (
                  <p className="dom-help-text">
                    {register_detail_expo_data?.registration_detail
                      ?.registration_heplpdesk_description ||
                      register_detail_expo_data?.registration_heplpdesk_description}
                  </p>
                )}

                {register_detail_expo_data?.registration_detail
                  ?.registration_helpdesks &&
                  register_detail_expo_data?.registration_detail
                    ?.registration_helpdesks.length > 0 && (
                    <div className="dom-contact-methods">
                      {register_detail_expo_data?.registration_detail?.registration_helpdesks
                        .filter((item) => item.title || item.detail)
                        .map((item, index) => (
                          <div
                            className="dom-contact-row"
                            key={item.id || index}
                          >
                            <div className="dom-c-icon">
                              <FaHeadset />
                            </div>
                            <div>
                              {item.title && (
                                <span className="dom-c-lbl">{item.title}</span>
                              )}
                              {item.detail && (
                                <span className="dom-c-val">{item.detail}</span>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
              </div>

              {/* Security Banner */}
              <div className="dom-sec-box">
                <FaShieldHalved className="dom-sec-icon" />
                <span>
                  Your details are 100% confidential and secure with Bharat
                  Buildcon.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DomesticVisitors;
